import { z } from "zod";
import {
  BudgetState,
  Count,
  Currency,
  EntitlementKind,
  IsoDate,
  IsoDateTime,
  LedgerType,
  Money,
  NonNegativeMoney,
  paged,
  PageQuery,
  Period,
  Share,
  Uuid,
} from "./common.ts";
import { ProductFamily } from "./catalog.ts";
import { OrderProduct } from "./orders.ts";

/*
 * Usage and billing (07 §2.2, §3.3 to §3.6, 06 `usage_item`, `ledger_entry`, `usage_report`,
 * UC-12 to UC-16, UC-25). Everything in USD (ADR-058); the conversion to EUR happens on the
 * invoice outside the platform. Open amounts and sums are computed, never stored (Grundsatz 2).
 */

/** `GET /api/usage/summary?period=YYYY-MM`; without `period` the current month. */
export const UsageSummaryQuery = z.object({
  period: Period.optional(),
});
export type UsageSummaryQuery = z.infer<typeof UsageSummaryQuery>;

/**
 * What OpenRouter reports for the entitlement's key so far this month, before any booking (G.1b,
 * TL 2026-10-14): the booked items reach yesterday at best and the last three days are still
 * open, so the view shows this next to them, provisional. Read by the API when the summary of the
 * current month is asked for, kept up to five minutes; never booked.
 */
export const LiveUsage = z.object({
  /** OpenRouter's `usage_monthly` of the active key: provider cost without markup, USD */
  cost: NonNegativeMoney,
  /** cost plus markup once MARKUP_PERCENT is set (R-06, F-18); null while it is not */
  amount: NonNegativeMoney.nullable(),
  /** when the API read it from OpenRouter */
  asOf: IsoDateTime,
});
export type LiveUsage = z.infer<typeof LiveUsage>;

/**
 * The live usage as the customer reads it (ADR-161 point 3): the amount with markup and when it was
 * read, never the cost without markup, from which the markup could be worked out.
 */
export const CustomerLiveUsage = z.strictObject({
  /** cost plus markup once MARKUP_PERCENT is set (R-06, F-18); null while it is not */
  amount: NonNegativeMoney.nullable(),
  /** when the API read it from OpenRouter */
  asOf: IsoDateTime,
});
export type CustomerLiveUsage = z.infer<typeof CustomerLiveUsage>;

/** Usage of one entitlement in the period, separated by demo (07 §3.4). */
export const UsageByProduct = z.object({
  entitlementId: Uuid,
  product: OrderProduct,
  kind: EntitlementKind,
  monthlyLimit: NonNegativeMoney,
  /**
   * runs over the platform's AI keys (ADR-144), as `Entitlement.aiAccess`: its order's AI access,
   * without an order its product's. Without it there is no monthly limit (zero), and the portal
   * shows "kein Monatslimit" in its place (ADR-144 addendum of 2026-10-30)
   */
  aiAccess: z.boolean(),
  /** provider cost before markup, from the usage items */
  baseCost: NonNegativeMoney,
  /** share of the service markup (R-06, F-18) */
  markup: NonNegativeMoney,
  /** base cost plus markup, what the ledger books */
  amount: NonNegativeMoney,
  /** false for demo usage: booked, reported, never invoiced (R-07) */
  billable: z.boolean(),
  requests: Count,
  promptTokens: Count,
  completionTokens: Count,
  reasoningTokens: Count,
  /**
   * The running month live and provisional (G.1b); null for another period, without an active
   * key, or when OpenRouter does not answer in time. Not part of the report.
   */
  live: LiveUsage.nullable(),
  /**
   * The available amount (ADR-149): `monthlyLimit` minus `live.amount`, what the key may still use
   * this month. Null while `live` or its amount is null, so for another period too; negative as it
   * is, after a lowered limit for one.
   */
  available: Money.nullable(),
  /**
   * `live.amount` divided by `monthlyLimit` (ADR-158 point F12.2-10, sub-point 2), the bar of the
   * entitlement; null without a live amount, so for another period too, and with a limit of 0
   */
  usedShare: Share.nullable(),
  /**
   * `monthlyLimit` divided by the customer's credit limit, the product's segment of the bar at the
   * credit limit (KA-87); null without a credit limit and for an entitlement that no longer lives,
   * whose limit no longer counts against it (R-04)
   */
  creditLimitShare: Share.nullable(),
});
export type UsageByProduct = z.infer<typeof UsageByProduct>;

/**
 * The month's budget (ADR-158 point F12.2-10 with the addendum of 2026-11-04): how much of the
 * monthly limits the live usage has used, how far the month is, where it ends at this pace and
 * whether that fits. The running month only, counted in UTC like `period` and OpenRouter's
 * `usage_monthly`; all four null for another month, without a live amount (`live.amount` null)
 * and while the sum of the monthly limits is 0. The portal shows them without computing anything.
 */
export const MonthBudget = z.object({
  /** `live.amount` divided by `limitSum` */
  usedShare: Share.nullable(),
  /** the days of the month so far, today included, divided by its days: the 15th of 30 is 0.5 */
  monthProgress: Share.nullable(),
  /** `live.amount` divided by the days so far, times the days of the month; with markup */
  forecast: NonNegativeMoney.nullable(),
  /** `forecast` against `limitSum` (`BudgetState`); null before the 3rd day of the month too */
  budgetState: BudgetState.nullable(),
});
export type MonthBudget = z.infer<typeof MonthBudget>;

/**
 * The live amount of one product family in the running month (ADR-158 point F12.2-10, sub-point
 * 1, AP-B14-03), "Wofür" in the popover of the capsule. Our families only: there are no partners
 * (ADR-018), and tools are no line of their own.
 */
export const UsageByFamily = z.object({
  /** the family from the catalogue by its concept id (ADR-112); null for the products without one */
  family: ProductFamily.nullable(),
  /** the live amount of the family's products, demos included, with markup */
  amount: NonNegativeMoney,
  /** `amount` divided by the summary's `live.amount`; the shares add up to 1 within rounding */
  share: Share,
});
export type UsageByFamily = z.infer<typeof UsageByFamily>;

export const UsageSummaryResponse = MonthBudget.extend({
  period: Period,
  currency: Currency,
  /** credit limit of the customer; null while registered or suspended (GC4, TL 2026-11-02) */
  creditLimit: NonNegativeMoney.nullable(),
  /** sum of the monthly limits of all living entitlements */
  limitSum: NonNegativeMoney,
  /**
   * "Noch frei" (ADR-158 point V1, AP-B14-10): `creditLimit` minus `limitSum`, what further monthly
   * limits may still take under R-04; not below zero; null while `creditLimit` is null
   */
  remaining: NonNegativeMoney.nullable(),
  /**
   * the live usage of the whole customer (addendum to ADR-145 of 2026-10-29): the sum over the
   * products whose entitlement has an active key, demos included, `asOf` the oldest reading,
   * `amount` null while one of them has none; null as soon as one of those keys gives no reading,
   * since the rest would show as the whole, and when none has a key, so for another period too
   * (addendum to ADR-145 of 2026-11-04, BB1). The file's `StaffCustomerFigures.live` is this value.
   * Null leaves `available`, `byFamily` and the month's budget null, the products keep theirs
   */
  live: LiveUsage.nullable(),
  /**
   * `limitSum` minus `live.amount` (ADR-149); null while `live` or its amount is null; negative as
   * it is
   */
  available: Money.nullable(),
  /**
   * the live amount per product family (`UsageByFamily`, AP-B14-03): the largest first, the
   * products without a family last, families with nothing left out; null while `live.amount` is
   * null, so for another period too
   */
  byFamily: z.array(UsageByFamily).nullable(),
  /** sum of all billable ledger entries of the customer, across periods (07 §3.4) */
  openAmount: Money,
  products: z.array(UsageByProduct),
});
export type UsageSummaryResponse = z.infer<typeof UsageSummaryResponse>;

/**
 * The usage of one entitlement as the customer reads it (ADR-161 point 3): `UsageByProduct`
 * without `baseCost` and `markup`, its live usage without the cost. Strict, so a field with a cost
 * that slips into the customer's answer fails at the API instead of leaving it.
 */
export const CustomerUsageByProduct = z.strictObject({
  ...UsageByProduct.omit({ baseCost: true, markup: true, live: true }).shape,
  live: CustomerLiveUsage.nullable(),
});
export type CustomerUsageByProduct = z.infer<typeof CustomerUsageByProduct>;

/**
 * `GET /api/usage/summary` (ADR-161 point 3): the summary as the customer reads it, every field of
 * `UsageSummaryResponse` but the costs without markup: no `baseCost` and `markup` per product, no
 * `cost` in the live usage. The file of the team keeps the whole `UsageSummaryResponse`
 * (`GET /api/staff/customers/:id/usage/summary`); both come from the same core function (ADR-145
 * point 1), the customer's view is made from it.
 */
export const CustomerUsageSummaryResponse = z.strictObject({
  ...UsageSummaryResponse.omit({ live: true, products: true }).shape,
  live: CustomerLiveUsage.nullable(),
  products: z.array(CustomerUsageByProduct),
});
export type CustomerUsageSummaryResponse = z.infer<typeof CustomerUsageSummaryResponse>;

/**
 * One usage item as OpenRouter reports it, per key, day, model and endpoint (06 `usage_item`), as
 * the customer reads it: with the amount, never the provider's cost (ADR-161 point 2). Strict, as
 * the customer's views are.
 */
export const UsageItem = z.strictObject({
  id: Uuid,
  entitlementId: Uuid,
  /** day in UTC */
  usageDate: IsoDate,
  model: z.string(),
  modelPermaslug: z.string(),
  endpointId: z.string(),
  providerName: z.string(),
  requests: Count,
  promptTokens: Count,
  completionTokens: Count,
  reasoningTokens: Count,
  /**
   * the provider's cost plus the item's markup by the rule of the booking (R-06: per item, rounded
   * half up, MARKUP_PERCENT); provisional like the item while its day is open; null without
   * MARKUP_PERCENT (F-18), like `live.amount`. Counted when read, with the markup in force: it does
   * not change (TL, 2026-11-04), so a booked item's amount is the ledger's. Should it change one
   * day, booked items take theirs from the ledger (BQ2)
   */
  amount: NonNegativeMoney.nullable(),
  /** true once the day is closed; then immutable */
  isFinal: z.boolean(),
});
export type UsageItem = z.infer<typeof UsageItem>;

/** `GET /api/usage/items`, newest day first. */
export const UsageItemsQuery = PageQuery.extend({
  period: Period.optional(),
  entitlementId: Uuid.optional(),
});
export type UsageItemsQuery = z.infer<typeof UsageItemsQuery>;

export const UsageItemsResponse = paged(UsageItem);
export type UsageItemsResponse = z.infer<typeof UsageItemsResponse>;

/**
 * An entry of the ledger (06 `ledger_entry`, R-05). Usage positive, corrections signed, an invoice
 * minus the open amount of its month: negative, positive for a credit note (AP-B11.3).
 */
export const LedgerEntry = z.object({
  id: Uuid,
  customerId: Uuid,
  type: LedgerType,
  amount: Money,
  baseCost: NonNegativeMoney.nullable(),
  markup: NonNegativeMoney.nullable(),
  billable: z.boolean(),
  entitlementId: Uuid.nullable(),
  usageItemId: Uuid.nullable(),
  /** the month the entry is booked into, the one report and invoice count it in */
  period: Period,
  /**
   * on a correction the month it corrects, never later than `period`; earlier when that month was
   * frozen and the API booked into the first open one (ADR-138 point 3); null on every other entry
   */
  correctedPeriod: Period.nullable(),
  invoiceReference: z.string().nullable(),
  /** mandatory on corrections */
  reason: z.string().nullable(),
  /** staff member on corrections and invoices */
  createdBy: Uuid.nullable(),
  createdAt: IsoDateTime,
});
export type LedgerEntry = z.infer<typeof LedgerEntry>;

/**
 * `GET /api/usage/ledger` (ADR-158 point F12.2-6, AP-B14-11): one month of the own customer's
 * ledger per read, paged, newest entry first; without `period` the running month. Any month can be
 * asked, one before the first entry is empty (TL, 2026-11-04).
 */
export const UsageLedgerQuery = PageQuery.extend({
  period: Period.optional(),
});
export type UsageLedgerQuery = z.infer<typeof UsageLedgerQuery>;

/**
 * An entry of the own ledger as the customer reads it (ADR-158 point F12.2-6): day, type, product,
 * reason, actor, amount. By the rules of ADR-117: a staff member is the team, without name or id.
 * Amounts only with markup, never the cost without it, nor the markup itself (ADR-121 addendum of
 * 2026-10-29); no balance, the open amount stays out of the customer's view (D-PA3). Strict, as the
 * customer's other views are: a `createdBy` or a cost that slips in fails at the API instead of
 * falling away unseen (BQ4).
 */
export const UsageLedgerEntry = z.strictObject({
  id: Uuid,
  type: LedgerType,
  /** when it was booked */
  createdAt: IsoDateTime,
  /** the month it is booked into, the one asked for */
  period: Period,
  /** on a correction the month it corrects (ADR-138 point 3); null on every other entry */
  correctedPeriod: Period.nullable(),
  /** the product of the entry's entitlement; null without one (an invoice, a correction for no product) */
  product: OrderProduct.nullable(),
  /** demo usage is booked and never invoiced (R-07); null without an entitlement */
  entitlementKind: EntitlementKind.nullable(),
  /** day (UTC) and model of the usage item; null on every type but `usage` */
  usage: z.strictObject({ usageDate: IsoDate, model: z.string() }).nullable(),
  /** the reason of a correction, which the customer reads in the history too (ADR-117); null otherwise */
  reason: z.string().nullable(),
  /**
   * `system` for usage the daily fetch booked, `staff` for what the team booked, shown as "Team".
   * Only these two book; `user` is no actor here, so a booking by a user would fail the schema
   * instead of showing as the team (BL4)
   */
  actor: z.enum(["system", "staff"]),
  /** with markup, signed as booked: a correction or an invoice may be negative */
  amount: Money,
  /** false for demo usage, which is never invoiced (R-07) */
  billable: z.boolean(),
  /** the number of the invoice on an invoice; null otherwise */
  invoiceReference: z.string().nullable(),
});
export type UsageLedgerEntry = z.infer<typeof UsageLedgerEntry>;

export const UsageLedgerResponse = paged(UsageLedgerEntry);
export type UsageLedgerResponse = z.infer<typeof UsageLedgerResponse>;

/** Usage of one product in the report, demo separated and marked non-billable (08 §7 rule 2). */
export const UsageReportProduct = UsageByProduct.pick({
  entitlementId: true,
  product: true,
  kind: true,
  baseCost: true,
  markup: true,
  amount: true,
  billable: true,
  requests: true,
  promptTokens: true,
  completionTokens: true,
  reasoningTokens: true,
});

/** Content of a usage report (06 `usage_report.content`, REQ-24). */
export const UsageReportContent = z.object({
  period: Period,
  currency: Currency,
  products: z.array(UsageReportProduct),
  /** corrections booked into the period, those of an earlier frozen month included (`correctedPeriod`) */
  corrections: z.array(LedgerEntry),
  totals: z.object({
    baseCost: NonNegativeMoney,
    markup: NonNegativeMoney,
    /** all usage of the period including demo */
    amount: NonNegativeMoney,
    /** usage that is invoiced */
    billableAmount: NonNegativeMoney,
    /** demo usage, shown and never invoiced (R-07) */
    nonBillableAmount: NonNegativeMoney,
    /** billable usage plus corrections; what the invoice covers */
    openAmount: Money,
  }),
});
export type UsageReportContent = z.infer<typeof UsageReportContent>;

export const UsageReport = z.object({
  id: Uuid,
  customerId: Uuid,
  period: Period,
  content: UsageReportContent,
  /** set with the invoice booking; afterwards the report is frozen (07 §3.6) */
  invoiceLedgerEntryId: Uuid.nullable(),
  /** when the invoice was booked, for "abgerechnet am …" (M13); null until then */
  invoicedAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
  frozenAt: IsoDateTime.nullable(),
});
export type UsageReport = z.infer<typeof UsageReport>;

/** A product of the report as the customer reads it (ADR-161 point 1): the amount, no cost and no markup. */
export const CustomerUsageReportProduct = z.strictObject(
  UsageReportProduct.omit({ baseCost: true, markup: true }).shape,
);
export type CustomerUsageReportProduct = z.infer<typeof CustomerUsageReportProduct>;

/**
 * The content of a report as the customer reads it (ADR-161 point 1): per product the amount,
 * billable, requests and tokens; the sums with markup only; the corrections as the entries of the
 * customer's ledger (`UsageLedgerEntry`, AP-B14-11), the team without name or id (ADR-117).
 */
export const CustomerUsageReportContent = z.strictObject({
  period: Period,
  currency: Currency,
  products: z.array(CustomerUsageReportProduct),
  /** corrections booked into the period, those of an earlier frozen month included (`correctedPeriod`) */
  corrections: z.array(UsageLedgerEntry),
  totals: z.strictObject({
    /** all usage of the period including demo */
    amount: NonNegativeMoney,
    /** usage that is invoiced */
    billableAmount: NonNegativeMoney,
    /** demo usage, shown and never invoiced (R-07) */
    nonBillableAmount: NonNegativeMoney,
    /** billable usage plus corrections; what the invoice covers */
    openAmount: Money,
  }),
});
export type CustomerUsageReportContent = z.infer<typeof CustomerUsageReportContent>;

/**
 * A usage report as the customer reads it (ADR-161 point 1): `UsageReport` with the customer's
 * content. Made from the team's report when it is read, in the core; the stored content of a frozen
 * report stays as it is. The team keeps `UsageReport` with every field.
 */
export const CustomerUsageReport = z.strictObject({
  ...UsageReport.omit({ content: true }).shape,
  content: CustomerUsageReportContent,
});
export type CustomerUsageReport = z.infer<typeof CustomerUsageReport>;

/** `GET /api/usage/reports/:period` (UC-13), the customer's report of that month (ADR-161 point 1). */
export const UsageReportResponse = CustomerUsageReport;
export type UsageReportResponse = z.infer<typeof UsageReportResponse>;
